import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Package, Truck, CheckCircle, Clock, MapPin, Phone, User } from "lucide-react";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API } from "../App";
import { format } from "date-fns";
import { motion } from "framer-motion";

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await axios.get(`${API}/orders/${orderId}`, { withCredentials: true });
        setOrder(response.data);
      } catch (error) {
        console.error("Error fetching order:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy • h:mm a');
    } catch {
      return dateString;
    }
  };

  const orderStatuses = ['pending', 'confirmed', 'packed', 'shipped', 'delivered'];
  
  const getStatusIndex = (status) => {
    if (status === 'cancelled') return -1;
    return orderStatuses.indexOf(status);
  };

  const getStatusIcon = (status, currentStatus) => {
    const currentIndex = getStatusIndex(currentStatus);
    const statusIndex = orderStatuses.indexOf(status);
    
    if (statusIndex < currentIndex) {
      return <CheckCircle className="w-4 h-4 text-white" />;
    } else if (statusIndex === currentIndex) {
      return <Clock className="w-4 h-4 text-white" />;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-24 pb-16 max-w-7xl mx-auto px-4">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-48 bg-card rounded"></div>
            <div className="h-64 bg-card rounded-xl"></div>
            <div className="h-48 bg-card rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">Order Not Found</h1>
          <Link to="/orders" className="text-primary hover:underline">Back to Orders</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const currentStatusIndex = getStatusIndex(order.order_status);

  return (
    <div className="min-h-screen bg-background" data-testid="order-detail-page">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/orders" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Orders
          </Link>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Order {order.order_id}</h1>
              <p className="text-muted-foreground mt-1">Placed on {formatDate(order.created_at)}</p>
            </div>
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium capitalize
              ${order.order_status === 'delivered' ? 'status-delivered' : 
                order.order_status === 'cancelled' ? 'status-cancelled' : 'status-confirmed'}`}
            >
              {order.order_status}
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              {/* Order Timeline */}
              {order.order_status !== 'cancelled' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-card border border-white/5 rounded-xl p-6"
                  data-testid="order-timeline"
                >
                  <h2 className="text-lg font-semibold text-foreground mb-6">Order Status</h2>
                  
                  <div className="space-y-6">
                    {orderStatuses.map((status, index) => {
                      const isCompleted = index < currentStatusIndex;
                      const isCurrent = index === currentStatusIndex;
                      
                      return (
                        <div key={status} className="timeline-item">
                          <div className={`timeline-dot ${isCompleted ? 'completed' : isCurrent ? 'active' : ''}`}>
                            {(isCompleted || isCurrent) && getStatusIcon(status, order.order_status)}
                          </div>
                          <div>
                            <p className={`font-medium capitalize ${isCompleted || isCurrent ? 'text-foreground' : 'text-muted-foreground'}`}>
                              {status}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {status === 'pending' && 'Order placed'}
                              {status === 'confirmed' && 'Payment confirmed'}
                              {status === 'packed' && 'Ready for dispatch'}
                              {status === 'shipped' && 'On the way'}
                              {status === 'delivered' && 'Delivered to you'}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* Order Items */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-card border border-white/5 rounded-xl p-6"
                data-testid="order-items"
              >
                <h2 className="text-lg font-semibold text-foreground mb-6">Order Items</h2>
                
                <div className="space-y-4">
                  {order.items.map((item) => (
                    <div key={item.product_id} className="flex gap-4 py-4 border-b border-white/5 last:border-0">
                      <div className="w-20 h-20 rounded-lg bg-background border border-white/5 flex items-center justify-center">
                        <Package className="w-8 h-8 text-muted-foreground" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-foreground">{item.name}</p>
                        <p className="text-sm text-muted-foreground font-mono">{item.sku}</p>
                        <div className="flex justify-between items-end mt-2">
                          <p className="text-sm text-muted-foreground">Qty: {item.quantity} × ₹{item.price}</p>
                          <p className="font-medium text-foreground">₹{item.subtotal}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Order Summary & Shipping */}
            <div className="space-y-6">
              {/* Order Summary */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-card border border-white/5 rounded-xl p-6"
                data-testid="order-summary"
              >
                <h2 className="text-lg font-semibold text-foreground mb-4">Order Summary</h2>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="text-foreground">₹{order.subtotal}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Shipping</span>
                    {order.shipping_fee === 0 ? (
                      <span className="text-green-500">FREE</span>
                    ) : (
                      <span className="text-foreground">₹{order.shipping_fee}</span>
                    )}
                  </div>
                  {order.discount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Discount</span>
                      <span className="text-green-500">-₹{order.discount}</span>
                    </div>
                  )}
                  <div className="border-t border-white/5 pt-3">
                    <div className="flex justify-between">
                      <span className="font-semibold text-foreground">Total</span>
                      <span className="text-xl font-bold text-foreground">₹{order.total}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-white/5">
                  <p className="text-sm text-muted-foreground">Payment Method</p>
                  <p className="font-medium text-foreground capitalize mt-1">{order.payment_method}</p>
                  <p className={`text-sm mt-1 ${order.payment_status === 'paid' ? 'text-green-500' : 'text-yellow-500'}`}>
                    {order.payment_status === 'paid' ? 'Payment Confirmed' : 'Payment Pending'}
                  </p>
                </div>
              </motion.div>

              {/* Shipping Address */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-card border border-white/5 rounded-xl p-6"
                data-testid="shipping-address"
              >
                <h2 className="text-lg font-semibold text-foreground mb-4">Shipping Address</h2>
                
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <User className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <p className="text-foreground">{order.shipping_address.full_name}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <p className="text-foreground">{order.shipping_address.phone}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <div className="text-muted-foreground text-sm">
                      <p>{order.shipping_address.address_line1}</p>
                      {order.shipping_address.address_line2 && <p>{order.shipping_address.address_line2}</p>}
                      <p>{order.shipping_address.city}, {order.shipping_address.state} - {order.shipping_address.pincode}</p>
                      <p>{order.shipping_address.country}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default OrderDetailPage;
